import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../App';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import { Textarea } from '../components/ui/textarea';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  BarChart3,
  TrendingUp,
  TrendingDown,
  DollarSign,
  FileSpreadsheet,
  Printer,
  Lock,
  Calculator,
  CheckSquare,
  FileText
} from 'lucide-react';

export default function ReportsPage() {
  const { API } = useAuth();
  const [loading, setLoading] = useState(true);
  const [reportData, setReportData] = useState(null);
  const [closings, setClosings] = useState([]);
  
  // Filters
  const [tipoRelatorio, setTipoRelatorio] = useState('mensal');
  const [mes, setMes] = useState(new Date().getMonth() + 1);
  const [ano, setAno] = useState(new Date().getFullYear());
  const [incluirVendidos, setIncluirVendidos] = useState(false);
  const [observacoes, setObservacoes] = useState('');
  const [observacoesRelatorio, setObservacoesRelatorio] = useState('');
  
  // Vehicle selection
  const [selectedVehicles, setSelectedVehicles] = useState([]);
  const [selectedDespesas, setSelectedDespesas] = useState([]);
  
  // Calculator
  const [percentual, setPercentual] = useState(50);
  const [calculatorTitle, setCalculatorTitle] = useState('Percentual');
  
  // Modal
  const [showClosingModal, setShowClosingModal] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    loadReport();
    loadClosings();
  }, [mes, ano, incluirVendidos]);

  const loadReport = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.append('mes', mes);
      params.append('ano', ano);
      params.append('incluir_vendidos', incluirVendidos);
      
      const response = await axios.get(`${API}/reports/financial?${params}`, { withCredentials: true });
      setReportData(response.data);
      // Select all vehicles and all general expenses by default (desmarcar exclui do total e do PDF)
      setSelectedVehicles(response.data.veiculos?.map(v => v.vehicle_id) || []);
      setSelectedDespesas(response.data.despesas_gerais?.map(d => d.id) || []);
    } catch (error) {
      toast.error('Erro ao carregar relatório');
    } finally {
      setLoading(false);
    }
  };

  const loadClosings = async () => {
    try {
      const response = await axios.get(`${API}/reports/closings`, { withCredentials: true });
      setClosings(response.data);
    } catch (error) {
      console.error('Error loading closings:', error);
    }
  };

  const handleClosing = async () => {
    setSubmitting(true);
    try {
      await axios.post(`${API}/reports/closing`, null, {
        params: { mes, ano, percentual, observacoes },
        withCredentials: true
      });
      toast.success('Fechamento mensal realizado com sucesso!');
      setShowClosingModal(false);
      loadClosings();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao realizar fechamento');
    } finally {
      setSubmitting(false);
    }
  };

  const toggleVehicle = (vehicleId) => {
    if (selectedVehicles.includes(vehicleId)) {
      setSelectedVehicles(selectedVehicles.filter(id => id !== vehicleId));
    } else {
      setSelectedVehicles([...selectedVehicles, vehicleId]);
    }
  };
  
  // A seleção guarda o id de cada despesa (não a posição), então continua certa
  // mesmo se a lista mudar de ordem ou de mês
  const toggleDespesa = (id) => {
    if (selectedDespesas.includes(id)) {
      setSelectedDespesas(selectedDespesas.filter(i => i !== id));
    } else {
      setSelectedDespesas([...selectedDespesas, id]);
    }
  };

  const toggleAllDespesas = () => {
    if (selectedDespesas.length === reportData?.despesas_gerais?.length) {
      setSelectedDespesas([]);
    } else {
      setSelectedDespesas(reportData.despesas_gerais?.map(d => d.id) || []);
    }
  };

  const toggleAll = () => {
    if (selectedVehicles.length === reportData?.veiculos?.length) {
      setSelectedVehicles([]);
    } else {
      setSelectedVehicles(reportData?.veiculos?.map(v => v.vehicle_id) || []);
    }
  };

  // Calculate totals based on selection
  const calculateTotals = () => {
    if (!reportData) return { entradas: 0, saidas: 0, saldo: 0 };
    
    const selected = reportData.veiculos?.filter(v => 
      selectedVehicles.length === 0 || selectedVehicles.includes(v.vehicle_id)
    ) || [];
    const entradas = selected.reduce((acc, v) => acc + v.entradas, 0);
    const saidas = selected.reduce((acc, v) => acc + v.saidas, 0);
    
    const selectedDespesasGerais = reportData.despesas_gerais?.filter(d =>
      selectedDespesas.includes(d.id)
    ) || [];
    const despesasGerais = selectedDespesasGerais.reduce((acc, d) => acc + d.valor, 0);
    
    return {
      entradas,
      saidas: saidas + despesasGerais,
      saldo: entradas - (saidas + despesasGerais)
    };
  };

  const totals = calculateTotals();
  const valorCalculado = totals.saldo * (percentual / 100);

  // Natural alphanumeric sorting function for vehicle IDs
  const parseVehicleId = (vehicleId) => {
    if (!vehicleId) return [999999, 0, 0, ''];
    
    const str = String(vehicleId).trim();
    // Match patterns like "01", "1.1", "1A", "02.2 YAMAHA", etc
    const match = str.match(/^(\d+)(?:\.(\d+)|([A-Za-z]))?(?:\s+(.*))?$/i);
    
    if (match) {
      const main = parseInt(match[1], 10);
      // For sorting: decimal numbers come before letters
      // 1.1 (subNum=1, subLetterOrder=0) < 1A (subNum=0, subLetterOrder=65)
      const subNum = match[2] ? parseInt(match[2], 10) : 0;
      const hasLetter = match[3] ? true : false;
      const subLetterOrder = match[3] ? match[3].toUpperCase().charCodeAt(0) : 0;
      const suffix = match[4] ? match[4].toLowerCase() : '';
      
      // Return: [main, hasLetter (0=number, 1=letter), subNum or letterOrder, suffix]
      // This ensures: 1 < 1.1 < 1A < 2 < 2.2 < 2A
      if (hasLetter) {
        return [main, 1, subLetterOrder, suffix]; // Letter suffix (1A, 2A, etc.)
      } else if (subNum > 0) {
        return [main, 0, subNum, suffix]; // Decimal suffix (1.1, 2.2, etc.)
      } else {
        return [main, -1, 0, suffix]; // No suffix (1, 2, 3, etc.)
      }
    }
    
    // Fallback: try to extract leading number
    const numMatch = str.match(/^(\d+)/);
    if (numMatch) {
      return [parseInt(numMatch[1], 10), -1, 0, str.toLowerCase()];
    }
    return [999999, 0, 0, str.toLowerCase()];
  };

  const sortVehiclesNaturally = (vehicles) => {
    if (!vehicles) return [];
    return [...vehicles].sort((a, b) => {
      const [aMain, aType, aSub, aSuffix] = parseVehicleId(a.vehicle_id);
      const [bMain, bType, bSub, bSuffix] = parseVehicleId(b.vehicle_id);
      
      if (aMain !== bMain) return aMain - bMain;
      if (aType !== bType) return aType - bType;
      if (aSub !== bSub) return aSub - bSub;
      return aSuffix.localeCompare(bSuffix);
    });
  };

  // Remove espaços sobrando e quebras de linha ("INTERNET " -> "INTERNET")
  const cleanText = (text) => (text || '').replace(/\s+/g, ' ').trim();

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value || 0);
  };

  const meses = [
    { value: 1, label: 'Janeiro' },
    { value: 2, label: 'Fevereiro' },
    { value: 3, label: 'Março' },
    { value: 4, label: 'Abril' },
    { value: 5, label: 'Maio' },
    { value: 6, label: 'Junho' },
    { value: 7, label: 'Julho' },
    { value: 8, label: 'Agosto' },
    { value: 9, label: 'Setembro' },
    { value: 10, label: 'Outubro' },
    { value: 11, label: 'Novembro' },
    { value: 12, label: 'Dezembro' }
  ];

  const anos = Array.from({ length: 5 }, (_, i) => new Date().getFullYear() - i);

  // Check if month is already closed
  const isMonthClosed = closings.some(c => c.mes === mes && c.ano === ano);

  // Export to PDF
  const exportToPDF = async () => {
    try {
      // Send selected vehicles to include only those in the PDF
      const vehiclesToInclude = selectedVehicles.length > 0 ? selectedVehicles : reportData.veiculos?.map(v => v.vehicle_id) || [];
      
      const response = await axios.get(`${API}/reports/pdf`, {
        params: { 
          mes, 
          ano, 
          percentual, 
          titulo_percentual: calculatorTitle,
          veiculos_selecionados: vehiclesToInclude.join(','),
          // Só as despesas marcadas entram no PDF (vazio = nenhuma)
          despesas_selecionadas: selectedDespesas.join(','),
          incluir_vendidos: incluirVendidos,
          observacoes: observacoesRelatorio
        },
        withCredentials: true,
        responseType: 'blob'
      });
      
      const mesesNomes = ['', 'janeiro', 'fevereiro', 'março', 'abril', 'maio', 'junho', 'julho', 'agosto', 'setembro', 'outubro', 'novembro', 'dezembro'];
      const filename = `relatorio_financeiro_${mesesNomes[mes]}_${ano}.pdf`;
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('PDF exportado com sucesso!');
    } catch (error) {
      toast.error('Erro ao exportar PDF');
      console.error('Error exporting PDF:', error);
    }
  };

  return (
    <div className="space-y-6" data-testid="reports-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold font-[Manrope]">Relatórios Financeiros</h1>
          <p className="text-gray-500 text-sm">Análise de rentabilidade e fechamento mensal</p>
        </div>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="grid grid-cols-1 md:grid-cols-6 gap-4 items-end">
            <div>
              <Label>Tipo</Label>
              <Select value={tipoRelatorio} onValueChange={setTipoRelatorio}>
                <SelectTrigger data-testid="tipo-relatorio">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="mensal">Mensal</SelectItem>
                  <SelectItem value="geral">Geral</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <Label>Mês</Label>
              <Select value={String(mes)} onValueChange={(v) => setMes(parseInt(v))}>
                <SelectTrigger data-testid="select-mes">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {meses.map(m => (
                    <SelectItem key={m.value} value={String(m.value)}>{m.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <Label>Ano</Label>
              <Select value={String(ano)} onValueChange={(v) => setAno(parseInt(v))}>
                <SelectTrigger data-testid="select-ano">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {anos.map(a => (
                    <SelectItem key={a} value={String(a)}>{a}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="flex items-center gap-2">
              <Checkbox 
                id="incluirVendidos" 
                checked={incluirVendidos}
                onCheckedChange={setIncluirVendidos}
              />
              <Label htmlFor="incluirVendidos" className="font-normal cursor-pointer text-sm">
                Incluir vendidos
              </Label>
            </div>
            
            <div>
              <Button onClick={loadReport} className="w-full" data-testid="gerar-relatorio">
                <BarChart3 className="w-4 h-4 mr-2" />
                Gerar Relatório
              </Button>
            </div>
            
            <div>
              <Button 
                onClick={exportToPDF} 
                variant="outline"
                className="w-full"
                data-testid="export-pdf-button"
              >
                <FileSpreadsheet className="w-4 h-4 mr-2" />
                Exportar PDF
              </Button>
            </div>
            
            <div>
              <Button 
                onClick={() => setShowClosingModal(true)} 
                variant="outline"
                className="w-full"
                disabled={isMonthClosed}
                data-testid="fechamento-button"
              >
                <Lock className="w-4 h-4 mr-2" />
                {isMonthClosed ? 'Mês Fechado' : 'Fechamento Mensal'}
              </Button>
            </div>
          </div>
          
          {isMonthClosed && (
            <div className="mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
              <p className="text-sm text-yellow-800">
                <Lock className="w-4 h-4 inline mr-1" />
                Este mês já possui fechamento realizado. Alterações em faturas deste período podem impactar a consistência dos dados.
              </p>
            </div>
          )}
        </CardContent>
      </Card>

      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main Content - Vehicle Balance */}
          <div className="lg:col-span-2 space-y-6">
            {/* Vehicle Table */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center justify-between">
                  <span>Balanço por Veículo</span>
                  <Button variant="ghost" size="sm" onClick={toggleAll}>
                    <CheckSquare className="w-4 h-4 mr-1" />
                    {selectedVehicles.length === reportData?.veiculos?.length ? 'Desmarcar Todos' : 'Selecionar Todos'}
                  </Button>
                </CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                {!reportData?.veiculos?.length ? (
                  <div className="p-8 text-center text-gray-500">
                    <BarChart3 className="w-12 h-12 mx-auto mb-2 opacity-50" />
                    <p>Nenhum dado para o período selecionado</p>
                  </div>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="w-12"></TableHead>
                        <TableHead>Descrição</TableHead>
                        <TableHead className="text-right">Entradas</TableHead>
                        <TableHead className="text-right">Saídas</TableHead>
                        <TableHead className="text-right">Saldo</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {sortVehiclesNaturally(reportData.veiculos).map((vehicle) => {
                        const isVendido = vehicle.status === 'vendido';
                        return (
                          <TableRow 
                            key={vehicle.vehicle_id}
                            className={isVendido && incluirVendidos ? 'bg-yellow-50 border-l-4 border-l-yellow-500' : ''}
                          >
                            <TableCell>
                              <Checkbox
                                checked={selectedVehicles.includes(vehicle.vehicle_id)}
                                onCheckedChange={() => toggleVehicle(vehicle.vehicle_id)}
                              />
                            </TableCell>
                            <TableCell>
                              <div className="flex items-center gap-2">
                                <div>
                                  <p className="font-medium">{vehicle.vehicle_id}</p>
                                  <p className="text-xs text-gray-500">{vehicle.vehicle_info}</p>
                                </div>
                                {isVendido && incluirVendidos && (
                                  <span className="px-2 py-0.5 text-xs bg-yellow-200 text-yellow-800 rounded-full">
                                    Vendido
                                  </span>
                                )}
                              </div>
                            </TableCell>
                            <TableCell className="text-right text-green-600">
                              {formatCurrency(vehicle.entradas)}
                            </TableCell>
                            <TableCell className="text-right text-red-600">
                              {formatCurrency(vehicle.saidas)}
                            </TableCell>
                            <TableCell className="text-right font-semibold">
                              {formatCurrency(vehicle.saldo)}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                      {/* Total Row */}
                      <TableRow className="bg-gray-50 font-semibold border-t-2">
                        <TableCell></TableCell>
                        <TableCell className="font-bold">TOTAL</TableCell>
                        <TableCell className="text-right text-green-600 font-bold">
                          {formatCurrency(
                            reportData.veiculos
                              .filter(v => selectedVehicles.length === 0 || selectedVehicles.includes(v.vehicle_id))
                              .reduce((acc, v) => acc + v.entradas, 0)
                          )}
                        </TableCell>
                        <TableCell className="text-right text-red-600 font-bold">
                          {formatCurrency(
                            reportData.veiculos
                              .filter(v => selectedVehicles.length === 0 || selectedVehicles.includes(v.vehicle_id))
                              .reduce((acc, v) => acc + v.saidas, 0)
                          )}
                        </TableCell>
                        <TableCell className="text-right font-bold">
                          {formatCurrency(
                            reportData.veiculos
                              .filter(v => selectedVehicles.length === 0 || selectedVehicles.includes(v.vehicle_id))
                              .reduce((acc, v) => acc + v.saldo, 0)
                          )}
                        </TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>

            {/* General Expenses */}
            {reportData?.despesas_gerais?.length > 0 && (
              <Card>
                <CardHeader className="flex flex-row items-center justify-between">
                  <CardTitle>Despesas Gerais</CardTitle>
                  <Button variant="outline" size="sm" onClick={toggleAllDespesas}>
                    <CheckSquare className="w-4 h-4 mr-2" />
                    {selectedDespesas.length === reportData?.despesas_gerais?.length ? 'Desmarcar Todas' : 'Selecionar Todas'}
                  </Button>
                </CardHeader>
                <CardContent className="p-0">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="w-12">
                          <Checkbox
                            checked={selectedDespesas.length === reportData?.despesas_gerais?.length}
                            onCheckedChange={toggleAllDespesas}
                          />
                        </TableHead>
                        <TableHead>Descrição</TableHead>
                        <TableHead className="text-right">Valor</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {/* Ordem e texto já vêm padronizados da API (a mesma do PDF) */}
                      {reportData.despesas_gerais.map((expense, idx) => (
                        <TableRow key={`${expense.id}-${idx}`} className={selectedDespesas.includes(expense.id) ? '' : 'opacity-50'}>
                          <TableCell>
                            <Checkbox
                              checked={selectedDespesas.includes(expense.id)}
                              onCheckedChange={() => toggleDespesa(expense.id)}
                              data-testid={`despesa-check-${expense.id}`}
                            />
                          </TableCell>
                          <TableCell>{cleanText(expense.descricao) || 'Despesa'}</TableCell>
                          <TableCell className="text-right text-red-600">
                            {formatCurrency(expense.valor)}
                          </TableCell>
                        </TableRow>
                      ))}
                      {/* Total Row */}
                      <TableRow className="bg-gray-50 font-semibold border-t-2">
                        <TableCell></TableCell>
                        <TableCell className="font-bold">TOTAL</TableCell>
                        <TableCell className="text-right text-red-600 font-bold">
                          {formatCurrency(
                            reportData.despesas_gerais
                              .filter(d => selectedDespesas.includes(d.id))
                              .reduce((acc, d) => acc + d.valor, 0)
                          )}
                        </TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>
            )}
          </div>

          {/* Sidebar - KPIs and Calculator */}
          <div className="space-y-6">
            {/* KPI Cards */}
            <Card className="bg-green-50 border-green-200">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-green-100 rounded-lg">
                    <TrendingUp className="w-5 h-5 text-green-600" />
                  </div>
                  <div>
                    <p className="text-sm text-green-700">Total Entradas</p>
                    <p className="text-2xl font-bold text-green-600">{formatCurrency(totals.entradas)}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <Card className="bg-red-50 border-red-200">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-red-100 rounded-lg">
                    <TrendingDown className="w-5 h-5 text-red-600" />
                  </div>
                  <div>
                    <p className="text-sm text-red-700">Total Saídas</p>
                    <p className="text-2xl font-bold text-red-600">{formatCurrency(totals.saidas)}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <Card className="bg-blue-50 border-blue-200">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-blue-100 rounded-lg">
                    <DollarSign className="w-5 h-5 text-blue-600" />
                  </div>
                  <div>
                    <p className="text-sm text-blue-700">Saldo Líquido</p>
                    <p className="text-2xl font-bold text-blue-600">{formatCurrency(totals.saldo)}</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Calculator */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Calculator className="w-5 h-5" />
                  Calculadora de Percentual
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label htmlFor="calculatorTitle">Título do Card</Label>
                  <Input
                    id="calculatorTitle"
                    value={calculatorTitle}
                    onChange={(e) => setCalculatorTitle(e.target.value)}
                    placeholder="Ex: Repasse, Lucro..."
                    data-testid="calculator-title"
                  />
                </div>
                
                <div>
                  <Label htmlFor="percentual">Percentual (%)</Label>
                  <Input
                    id="percentual"
                    type="number"
                    min="0"
                    max="100"
                    value={percentual}
                    onChange={(e) => setPercentual(parseFloat(e.target.value) || 0)}
                    data-testid="calculator-percentual"
                  />
                </div>
                
                <div className="p-4 bg-purple-50 rounded-lg">
                  <p className="text-sm text-purple-700">{calculatorTitle}</p>
                  <p className="text-2xl font-bold text-purple-600">{formatCurrency(valorCalculado)}</p>
                  <p className="text-xs text-purple-500">{percentual}% do saldo líquido</p>
                </div>
              </CardContent>
            </Card>

            {/* Observações do Relatório */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <FileText className="w-5 h-5" />
                  Observações
                </CardTitle>
              </CardHeader>
              <CardContent>
                <Textarea
                  id="observacoes-relatorio"
                  data-testid="observacoes-relatorio"
                  value={observacoesRelatorio}
                  onChange={(e) => setObservacoesRelatorio(e.target.value)}
                  placeholder="Adicione notas ou observações que serão incluídas no PDF..."
                  rows={4}
                  className="resize-none"
                />
              </CardContent>
            </Card>

            {/* Export Buttons */}
            <Card>
              <CardHeader>
                <CardTitle>Exportar</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                <Button variant="outline" className="w-full justify-start">
                  <FileSpreadsheet className="w-4 h-4 mr-2" />
                  Exportar Excel
                </Button>
                <Button variant="outline" className="w-full justify-start">
                  <FileSpreadsheet className="w-4 h-4 mr-2" />
                  Exportar PDF
                </Button>
                <Button variant="outline" className="w-full justify-start">
                  <Printer className="w-4 h-4 mr-2" />
                  Imprimir
                </Button>
              </CardContent>
            </Card>
          </div>
        </div>
      )}

      {/* Closing Modal */}
      <Dialog open={showClosingModal} onOpenChange={setShowClosingModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Realizar Fechamento Mensal</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-gray-600">
              Você está prestes a realizar o fechamento de <strong>{meses.find(m => m.value === mes)?.label}/{ano}</strong>.
              Essa ação registrará os valores atuais do relatório.
            </p>
            
            <div className="bg-gray-50 p-4 rounded-lg space-y-2">
              <div className="flex justify-between">
                <span className="text-gray-500">Total Entradas:</span>
                <span className="font-medium text-green-600">{formatCurrency(totals.entradas)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">Total Saídas:</span>
                <span className="font-medium text-red-600">{formatCurrency(totals.saidas)}</span>
              </div>
              <div className="flex justify-between border-t pt-2">
                <span className="text-gray-500">Saldo Líquido:</span>
                <span className="font-bold text-blue-600">{formatCurrency(totals.saldo)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">{calculatorTitle} ({percentual}%):</span>
                <span className="font-medium text-purple-600">{formatCurrency(valorCalculado)}</span>
              </div>
            </div>
            
            <div>
              <Label htmlFor="obs_fechamento">Observações</Label>
              <Textarea
                id="obs_fechamento"
                value={observacoes}
                onChange={(e) => setObservacoes(e.target.value)}
                placeholder="Observações adicionais..."
                rows={3}
              />
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowClosingModal(false)}>
                Cancelar
              </Button>
              <Button 
                onClick={handleClosing} 
                disabled={submitting}
                data-testid="confirm-fechamento"
              >
                {submitting ? 'Processando...' : 'Confirmar Fechamento'}
              </Button>
            </DialogFooter>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
